import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { PaginatedInvoiceListList, UrlParams } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface IUseInvoices {
  search?: string;
  autoPay?: string | null;
  status?: string[] | null;
  athlete?: string;
  athletePackage?: string;
  dueDateStart?: string;
  dueDateEnd?: string;
  ordering?: string;
  enabled?: boolean;
  billingSources?: string[];
  paymentDateStart?: string;
  paymentDateEnd?: string;
  productType?: string | null;
  paymentMethods?: string[];
  csvExport?: string;
  limit?: string;
}

interface QueryKeyType {
  pageParam?: UrlParams;
  queryKey: (null | string | boolean | undefined | string[])[];
}

function useInvoices({
  search,
  status,
  athlete,
  athletePackage,
  dueDateStart,
  dueDateEnd,
  paymentDateStart,
  paymentDateEnd,
  productType,
  billingSources,
  paymentMethods,
  autoPay,
  ordering,
  limit = '20',
  csvExport,
  enabled = true,
}: IUseInvoices): UseInfiniteQueryResult<AxiosResponse<PaginatedInvoiceListList>> {
  const fetchInvoices = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedInvoiceListList>> => {
      const params = pageParam;

      if (search) {
        params.q = search;
      }
      if (autoPay) {
        params.auto_pay = autoPay;
      }
      if (!!status?.length) {
        params.status = status.join(',');
      }
      if (athlete) {
        params.athlete = athlete;
      }
      if (athletePackage) {
        params.athlete_package = athletePackage;
      }
      if (dueDateStart !== undefined) {
        params.due_date_start = dueDateStart;
      }
      if (dueDateEnd !== undefined) {
        params.due_date_end = dueDateEnd;
      }
      if (paymentDateStart !== undefined) {
        params.payment_date_start = paymentDateStart;
      }
      if (paymentDateEnd !== undefined) {
        params.payment_date_end = paymentDateEnd;
      }
      if (productType) {
        params.product_type = productType;
      }
      if (billingSources && billingSources.length > 0) {
        params.billing_sources = billingSources.join(',');
      }
      if (paymentMethods && paymentMethods.length > 0) {
        params.payment_methods = paymentMethods.join(',');
      }
      if (ordering) {
        if (ordering === 'athlete' || ordering === '-athlete') {
          params.ordering = `${ordering}__first_name`;
        } else {
          params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`).concat(',pk');
        }
      }
      return client.get(`invoices/?${encodeUrlParams(params)}`);
    },
    [
      search,
      autoPay,
      status,
      athlete,
      athletePackage,
      dueDateStart,
      dueDateEnd,
      paymentDateStart,
      paymentDateEnd,
      productType,
      billingSources,
      paymentMethods,
      ordering,
      limit,
    ],
  );

  return useInfiniteQuery(
    [
      'invoices',
      search,
      autoPay,
      status,
      athlete,
      athletePackage,
      dueDateStart,
      dueDateEnd,
      paymentDateStart,
      paymentDateEnd,
      productType,
      billingSources,
      paymentMethods,
      ordering,
      csvExport,
    ],
    fetchInvoices,
    {
      getNextPageParam: page => {
        return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
      },
      enabled,
    },
  );
}

export default useInvoices;
